<template>
  <div class="page-stack">
    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Users</div>
          <h3 class="section-title">用户管理</h3>
          <p class="section-subtitle">默认查看用户列表。新增和编辑均通过模态框完成。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <input v-model.trim="keyword" class="field-input min-w-[220px]" placeholder="搜索用户名、昵称或角色">
          <button class="btn btn-primary" @click="openCreateModal">新建用户</button>
          <button class="btn btn-secondary" @click="loadUsers">刷新列表</button>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Members</div>
          <h3 class="section-title">用户列表</h3>
          <p class="section-subtitle">默认管理员和运营账号会在后端启动时自动初始化。</p>
        </div>
        <div class="text-sm text-slate-400">共 {{ pagination.total }} 条</div>
      </div>
      <div class="table-shell mt-6 overflow-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>用户名</th>
              <th>昵称</th>
              <th>角色</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in users" :key="item.id">
              <td class="font-semibold text-slate-900">{{ item.username }}</td>
              <td>{{ item.nickname }}</td>
              <td>{{ item.role }}</td>
              <td>
                <span class="badge" :class="item.status === 1 ? 'badge-success' : 'badge-muted'">
                  {{ item.status === 1 ? '启用' : '禁用' }}
                </span>
              </td>
              <td class="text-sm text-slate-500">{{ item.createdAt || '-' }}</td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="btn btn-secondary" @click="openEditModal(item)">编辑</button>
                  <button class="btn btn-secondary" @click="toggleStatus(item)">
                    {{ item.status === 1 ? '禁用' : '启用' }}
                  </button>
                  <button class="btn btn-secondary" @click="resetPassword(item.id)">重置密码</button>
                </div>
              </td>
            </tr>
            <tr v-if="!users.length">
              <td colspan="6" class="table-empty">当前没有用户记录</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        :page-num="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :total-pages="pagination.totalPages"
        @change-page="handlePageChange"
        @change-size="handlePageSizeChange"
      />
    </section>

    <div v-if="modalVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">{{ form.id ? '编辑用户' : '新建用户' }}</h4>
            <p class="mt-2 text-sm text-slate-500">维护账号、角色、状态和密码信息。</p>
          </div>
          <button class="btn btn-secondary" @click="closeModal">关闭</button>
        </div>

        <div class="modal-panel__body mt-6 grid gap-4 md:grid-cols-2">
          <div>
            <label class="field-label">用户名</label>
            <input v-model.trim="form.username" class="field-input" :disabled="Boolean(form.id)" placeholder="例如：designer01">
          </div>
          <div>
            <label class="field-label">昵称</label>
            <input v-model.trim="form.nickname" class="field-input" placeholder="例如：商品运营">
          </div>
          <div>
            <label class="field-label">角色</label>
            <select v-model="form.role" class="field-select">
              <option value="ADMIN">ADMIN</option>
              <option value="OPERATOR">OPERATOR</option>
            </select>
          </div>
          <div>
            <label class="field-label">状态</label>
            <select v-model.number="form.status" class="field-select">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
          <div class="md:col-span-2">
            <label class="field-label">密码 {{ form.id ? '（留空则不修改）' : '' }}</label>
            <input v-model.trim="form.password" type="password" class="field-input" placeholder="默认 123456">
          </div>
        </div>

        <div class="modal-panel__footer">
          <button class="btn btn-primary" @click="submitUser">{{ form.id ? '更新用户' : '保存用户' }}</button>
          <button class="btn btn-secondary" @click="resetForm">清空</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue';
import AppPagination from '@/components/AppPagination.vue';
import { userApi } from '@/api';
import { confirmAction, notifyError, notifySuccess } from '@/utils/feedback';

const users = ref([]);
const keyword = ref('');
const modalVisible = ref(false);
const pagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0
});
const form = reactive({
  id: null,
  username: '',
  nickname: '',
  role: 'OPERATOR',
  status: 1,
  password: ''
});

function resetForm() {
  form.id = null;
  form.username = '';
  form.nickname = '';
  form.role = 'OPERATOR';
  form.status = 1;
  form.password = '';
}

function openCreateModal() {
  resetForm();
  modalVisible.value = true;
}

function openEditModal(item) {
  form.id = item.id;
  form.username = item.username;
  form.nickname = item.nickname;
  form.role = item.role;
  form.status = item.status;
  form.password = '';
  modalVisible.value = true;
}

function closeModal() {
  modalVisible.value = false;
}

async function loadUsers() {
  try {
    const res = await userApi.list({
      keyword: keyword.value || undefined,
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize
    });
    users.value = res.data.records || [];
    pagination.total = res.data.total || 0;
    pagination.totalPages = res.data.totalPages || 0;
    if (!users.value.length && pagination.pageNum > 1 && pagination.total > 0) {
      pagination.pageNum -= 1;
      await loadUsers();
    }
  } catch (error) {
    notifyError(error.message || '加载用户失败');
  }
}

async function submitUser() {
  if (!form.username || !form.nickname) {
    notifyError('请填写完整信息');
    return;
  }
  const isEdit = Boolean(form.id);
  const payload = {
    username: form.username,
    nickname: form.nickname,
    role: form.role,
    status: form.status,
    password: form.password
  };
  try {
    if (form.id) {
      await userApi.update(form.id, payload);
    } else {
      await userApi.create(payload);
    }
    closeModal();
    resetForm();
    await loadUsers();
    notifySuccess(isEdit ? '用户已更新' : '用户已保存');
  } catch (error) {
    notifyError(error.message || '保存用户失败');
  }
}

async function toggleStatus(item) {
  try {
    await userApi.updateStatus(item.id, item.status === 1 ? 0 : 1);
    await loadUsers();
    notifySuccess(item.status === 1 ? '用户已禁用' : '用户已启用');
  } catch (error) {
    notifyError(error.message || '更新状态失败');
  }
}

async function resetPassword(id) {
  if (!(await confirmAction({
    title: '重置密码',
    message: '确认把密码重置为 123456 吗？',
    confirmText: '确认重置',
    tone: 'danger'
  }))) {
    return;
  }
  try {
    await userApi.resetPassword(id);
    notifySuccess('密码已重置为 123456');
  } catch (error) {
    notifyError(error.message || '重置密码失败');
  }
}

function handlePageChange(pageNum) {
  pagination.pageNum = pageNum;
  loadUsers();
}

function handlePageSizeChange(pageSize) {
  pagination.pageSize = pageSize;
  pagination.pageNum = 1;
  loadUsers();
}

watch(keyword, () => {
  pagination.pageNum = 1;
  loadUsers();
});

onMounted(loadUsers);
</script>
